/*
 * Copyright (c) 2024 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */
import { RedlineDatasetIdToData } from "../../../redux/ducks/CoverageRequirements/slice";
import { EDatasetExecutionStatus, MERGED_DATASET_TREE_TYPE, SINGLE_DATASET_TREE_TYPE } from "./ItemDetails/constants";
import { getNodeById } from "./ItemDetails/Tabs/CoverageRequirementsTab/LeftPanelComponents/TreeComponent/CoverageRequirementsTreeComponent";
import { DashboardRow, RedlineDataset } from "./ItemDetails/Tabs/ExecutionStatusTab/constants";

export const getStudyNameById = (studyIdToName: any, studyId: string) => {
    return studyIdToName?.[studyId] ?? studyId
}

/**
 * Get the path to the node for the dataset
 */
export const getNodeIdByDatasetId = (nodes: any, datasetId: any) => {
    if (nodes?.datasetId === datasetId) {
        return nodes
    } else if (Array.isArray(nodes?.children)) {
        let result = null;
        nodes?.children.forEach((node: any) => {
            if (!!getNodeIdByDatasetId(node, datasetId)) {
                result = getNodeIdByDatasetId(node, datasetId)
            }
        });
        return result
    }
    return null
}

export const getPathToNode: any = (nodeId: any, tree: any) => getPathsToAllNodes(tree).filter((r: any) => r[r.length - 1] == nodeId)[0]
export const getPathsToAllNodes: any = (nodes: any) => {
    return (
        nodes?.children == undefined || nodes?.children?.length == 0) ? [[nodes?.nodeId]]
        : nodes?.children.flatMap(getPathsToAllNodes).map((r: any) => [nodes?.nodeId, ...r]
        )
}
export const pathNodesToNames = (path: string[], tree: any) => {
    let res: string[] = []
    path?.forEach((nodeId) => {
        const name = nodeIdToName(tree, nodeId)
        if (name != null) {
            res.push(name)
        }
    })
    return res
}

export const nodeIdToName = (nodes: any, id: string) => {
    const node = getNodeById(nodes, id)
    if (node != null) {
        const name = node.name
        return name
    }
    return null
}

/**
 * Processes the redline data and returns an array of dashboard rows
 * to be used by a table.
 * @param dataset - Queried datasets.
 * @param propsStudyId - The study ID from props.
 * @param rawRedlineTreeDbItem - The raw redline tree database item.
 * @param mergedRedlineTreeDbItem - The merged redline tree database item.
 * @param dashboardTableRows - The array of dashboard rows (optional).
 * @returns Either null or an array of dashboard rows.
 */
export const processRedlineData = (
    propsStudyId: string | null,
    dataset: RedlineDatasetIdToData | null,
    rawRedlineTreeDbItem: any,
    mergedRedlineTreeDbItem: any,
    datasetExecutionStatusMap: any,
    datasetsLocalState: any,
    prevDashboardTableRows: DashboardRow[] | null,
    datasetExecutionStatusFilter?: string, // only show datasets with this execution status
    gmid?: string,
    isLandingPage?: boolean,
    userIdNameMap?: any
): null | DashboardRow[] => {
    if (dataset == null || datasetExecutionStatusMap == null) return null

    const dashboardTableRows: DashboardRow[] = []

    // TODO: If we are in single study not home page, and we fetched all redline datasets, 
    // then we need to filter out the ones that are not for the current study. If we only fetched redline datasets 
    // for the current study, but dashboard uses same state,
    // then we'd either need to clear the state at some point or filter out, or we could wait for the network call 
    // and replace and while that call is happening, dont show the data or do any calculations with it.

    Object.keys(dataset).forEach((redlineDatasetId: string) => {
        const redlineDataset: RedlineDataset = dataset[redlineDatasetId]
        if (redlineDataset == null ||
            (propsStudyId && redlineDataset.referencedBy?.length > 0 && !redlineDataset.referencedBy.includes(propsStudyId)) ||
            (datasetExecutionStatusFilter && (redlineDataset.executionStatus !== datasetExecutionStatusFilter))
        ) return

        const generator = redlineDataset.generator
        if (isLandingPage && gmid && generator != gmid) return

        const studyId = propsStudyId ?? redlineDataset.referencedBy[0]

        let datasetProgress = datasetsLocalState?.[redlineDatasetId]?.progress
        if (datasetProgress)
            datasetProgress = datasetProgress.toString()
        const executionStatus: EDatasetExecutionStatus = datasetExecutionStatusMap[redlineDatasetId]
        let endTime = redlineDataset.endTime ?? ""
        let runTime = redlineDataset.runTime ?? ""
        if (executionStatus === EDatasetExecutionStatus.IN_PROGRESS) {
            endTime = ""
            runTime = ""
        }

        let pathString = ''

        // Get either the raw or merged tree data depending on the redline type
        if (redlineDatasetId != null && redlineDatasetId?.length > 0) {
            let treeData
            if (redlineDataset.redlineType === SINGLE_DATASET_TREE_TYPE) {
                if (propsStudyId == null) {
                    treeData = rawRedlineTreeDbItem?.[studyId]?.[SINGLE_DATASET_TREE_TYPE]?.tree
                }
                else
                    treeData = rawRedlineTreeDbItem?.tree
            } else if (redlineDataset.redlineType === MERGED_DATASET_TREE_TYPE) {
                if (propsStudyId == null)
                    treeData = mergedRedlineTreeDbItem?.[studyId]?.[MERGED_DATASET_TREE_TYPE]?.tree
                else
                    treeData = mergedRedlineTreeDbItem?.tree
            } else {
                return dashboardTableRows
            }

            // Get the path to the node for the dataset
            const nodeId = getNodeIdByDatasetId(treeData, redlineDatasetId)?.nodeId
            const path = pathNodesToNames(getPathToNode(nodeId, treeData), treeData)
            path.shift()
            const pathStringOld = path.toString()
            pathString = pathStringOld.replace(/[,]/g, '/')
        }

        const dsp = redlineDataset.driving_scenario_properties
        const row: DashboardRow = {
            studyId: propsStudyId ?? studyId,
            id: redlineDataset.id,
            type: redlineDataset.redlineType,
            name: redlineDataset.name,
            path: pathString,
            roadtype: dsp ? dsp.filter((property) => property.type === 'Road Type') : [],
            object: dsp ? dsp.filter((property) => property.type === 'Object') : [],
            package: dsp ? dsp.filter((property) => property.type === 'Package') : [],
            capability: dsp ? dsp.filter((property) => property.type === 'Capability') : [],
            usecase: dsp ? dsp.filter((property) => property.type === 'Use Case') : [],
            modality: dsp ? dsp.filter((property) => property.type === 'Modality') : [],
            perceptiondataflow: dsp ? dsp.filter((property) => property.type === 'Perception Data Flow') : [],
            safety: dsp ? dsp.filter((property) => property.type === 'Safety (ASIL Rating)') : [],
            description: redlineDataset.description,
            creator: redlineDataset.creator,
            owner: redlineDataset.owner,
            generator: userIdNameMap ? (userIdNameMap[generator] ?? '') : redlineDataset.generator,
            status: redlineDataset.executionStatus,
            dateCreated: redlineDataset.dateCreated,
            dateModified: redlineDataset.dateModified,
            dateGenerated: redlineDataset.dateGenerated ?? "",
            progress: datasetProgress ?? "",
            runTime: runTime,
            endTime: endTime,
        }
        dashboardTableRows.push(row);
    })
    return dashboardTableRows;
}

